/* generated using openapi-typescript-codegen -- do no edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { EmailResult } from '../models/EmailResult';
import type { ExternalSourceSync } from '../models/ExternalSourceSync';
import type { Project } from '../models/Project';
import type { ProvisionProjectRequest } from '../models/ProvisionProjectRequest';
import type { ProvisionProjectResponse } from '../models/ProvisionProjectResponse';
import type { ProvisionProjectUsingAudioFilesRequest } from '../models/ProvisionProjectUsingAudioFilesRequest';
import type { ProvisionProjectUsingAudioFilesResponse } from '../models/ProvisionProjectUsingAudioFilesResponse';
import type { ProvisionProjectUsingExternalDataSourceRequest } from '../models/ProvisionProjectUsingExternalDataSourceRequest';
import type { ProvisionProjectUsingExternalDataSourceResponse } from '../models/ProvisionProjectUsingExternalDataSourceResponse';
import type { UpdateMetaColumnNameRequest } from '../models/UpdateMetaColumnNameRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class ProjectsService {

    /**
     * Cancels the project.  In dev, it will just set the status to `Ready` and the
     * background service will pick it up.  In prod, we do that and kick off a
     * Cloud Run job.
     * @param projectId The ID of the project to run.
     * @returns any OK
     * @throws ApiError
     */
    public static cancelProject(
        projectId: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/projects/{projectId}/cancel',
            path: {
                'projectId': projectId,
            },
        });
    }

    /**
     * Deletes the specified project.
     * @param projectId The ID of the project to run.
     * @returns any OK
     * @throws ApiError
     */
    public static deleteProject(
        projectId: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/projects/{projectId}/delete',
            path: {
                'projectId': projectId,
            },
        });
    }

    /**
     * Downloads the results of the project as a CSV.
     * @param projectId The ID of the project to run.
     * @param includeMeta When true, includes the meta fields
     * @returns any OK
     * @throws ApiError
     */
    public static downloadResults(
        projectId: string,
        includeMeta: boolean = true,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/projects/{projectId}/downloadresults',
            path: {
                'projectId': projectId,
            },
            query: {
                'includeMeta': includeMeta,
            },
        });
    }

    /**
     * Retrieve all projects for the specified workspace.
     * @param workspaceId The ID of the workspace to scope the projects for.
     * @returns ExternalSourceSync OK
     * @throws ApiError
     */
    public static externalSourceSyncStatus(
        workspaceId?: string,
    ): CancelablePromise<Array<ExternalSourceSync>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/projects/status/external',
            query: {
                'workspaceId': workspaceId,
            },
        });
    }

    /**
     * Get the paginated results of the project
     * @param projectId The ID of the project to run.
     * @param includeMeta When true, includes the meta fields
     * @param page The page number for pagination
     * @param limit The number of rows per page
     * @param sortColumn The column to sort by. Sorting only applies when this is "DATE".
     * @param sortOrder The sort order ("asc" or "desc"). Defaults to ascending.
     * @param searchTerm The search term for case-insensitive search.
     * @returns any OK
     * @throws ApiError
     */
    public static getResults(
        projectId: string,
        includeMeta: boolean = true,
        page?: number,
        limit: number = 10,
        sortColumn?: string,
        sortOrder: string = 'asc',
        searchTerm?: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/projects/{projectId}/results',
            path: {
                'projectId': projectId,
            },
            query: {
                'includeMeta': includeMeta,
                'page': page,
                'limit': limit,
                'sortColumn': sortColumn,
                'sortOrder': sortOrder,
                'searchTerm': searchTerm,
            },
        });
    }

    /**
     * Provisions a project using the uploaded CSV file and information.
     * @param requestBody The request to create a project.
     * @returns ProvisionProjectResponse OK
     * @throws ApiError
     */
    public static provisionProject(
        requestBody?: ProvisionProjectRequest,
    ): CancelablePromise<ProvisionProjectResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/projects/provision',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Provisions a project using the uploaded CSV file and information.
     * @param requestBody The request to create a project.
     * @returns ProvisionProjectUsingAudioFilesResponse OK
     * @throws ApiError
     */
    public static provisionProjectUsingAudioFiles(
        requestBody?: ProvisionProjectUsingAudioFilesRequest,
    ): CancelablePromise<ProvisionProjectUsingAudioFilesResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/projects/provision/audio',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Provisions a project and starts a cloudrun job to import data from an external source.
     * @param requestBody The request to create a project.
     * @returns ProvisionProjectUsingExternalDataSourceResponse OK
     * @throws ApiError
     */
    public static provisionProjectUsingExternalDataSource(
        requestBody?: ProvisionProjectUsingExternalDataSourceRequest,
    ): CancelablePromise<ProvisionProjectUsingExternalDataSourceResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/projects/provision/external',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Reports an error in the project data
     * @param projectId The ID of the project that had the error.
     * @param rowId The id of the row that had the error.
     * @param columnId The id of the column that had the error.
     * @param originalValue The original value of the cell
     * @param newValue The new value of the cell
     * @param orgId The ID of the organization
     * @returns EmailResult OK
     * @throws ApiError
     */
    public static reportError(
        projectId: string,
        rowId?: string,
        columnId?: string,
        originalValue?: string,
        newValue?: string,
        orgId?: string,
    ): CancelablePromise<EmailResult> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/projects/{projectId}/reporterror',
            path: {
                'projectId': projectId,
            },
            query: {
                'rowId': rowId,
                'columnId': columnId,
                'originalValue': originalValue,
                'newValue': newValue,
                'orgId': orgId,
            },
        });
    }

    /**
     * Runs the project.  In dev, it will just set the status to `Ready` and the
     * background service will pick it up.  In prod, we do that and kick off a
     * Cloud Run job.
     * @param projectId The ID of the project to run.
     * @returns any OK
     * @throws ApiError
     */
    public static runProject(
        projectId: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/projects/{projectId}/run',
            path: {
                'projectId': projectId,
            },
        });
    }

    /**
     * Updates a particular meta column's name in all the rows of the project.
     * @param requestBody The request to create a project.
     * @returns any OK
     * @throws ApiError
     */
    public static updateMetaColumnName(
        requestBody?: UpdateMetaColumnNameRequest,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/projects/metaColumn/update',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Retrieve all projects for the specified workspace, excluding those created via the external API.
     * @param workspaceId The ID of the workspace to scope the projects for.
     * @returns Project OK
     * @throws ApiError
     */
    public static getProjects(
        workspaceId: string,
    ): CancelablePromise<Array<Project>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/projects/{workspaceId}',
            path: {
                'workspaceId': workspaceId,
            },
        });
    }

}
